import React, { useContext, useState, useEffect } from "react";
import api from "../../../services/AxiosConnection";
import AuthContext from "../../../context/AuthContext";

function SelfTest() {
  const { selectedSite } = useContext(AuthContext);

  const [formValues, setFormValues] = useState({
    TypeOfLoco: "",
    NoOfLoco: "",
    NoOfCoaches: "",
    Description: "WAGON",
    Speed: "",
    AtmosphericTemp: "",
    axles: [],
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [testResult, setTestResult] = useState(null);
  const [showForm, setShowForm] = useState(true);
  const [lastTestTimestamp, setLastTestTimestamp] = useState("N/A");
  const [timeDifference, setTimeDifference] = useState("N/A");
  const [maxAxles, setMaxAxles] = useState(0);
  const [showAxleFields, setShowAxleFields] = useState(false);
  const [axleNumbers, setAxleNumbers] = useState([]);
  const [sensorCount, setSensorCount] = useState(2); // Default to 6 as per backend default

  const defectTypeOptions = [
    { value: "Axle Warning", minSensors: 2 },
    { value: "Axle Alarm", minSensors: 2 },
    { value: "Wheel Warning", minSensors: 4 },
    { value: "Wheel Alarm", minSensors: 4 },
    { value: "Brake Disc Warning", minSensors: 6 },
    { value: "Brake Disc Alarm", minSensors: 6 },
  ];

  const getFilteredDefectTypes = () => {
    return defectTypeOptions.filter((option) => sensorCount >= option.minSensors);
  };

  const formatTrainDateTime = (trainId) => {
    if (!trainId) return "N/A";
    const parts = trainId.split("_");
    if (parts.length !== 7) return trainId;
    const [year, month, day, hour, minute, second] = parts;
    return `${day}/${month}/${year} ${hour}:${minute}:${second}`;
  };

  const formatTrainId = (trainId) => {
    if (!trainId) return "N/A";
    const parts = trainId.split("_");
    if (parts.length !== 7) return trainId;
    const [year, month, day, hour, minute] = parts;
    return `${day}/${month}/${year} ${hour}:${minute}`;
  };

  const parseTimestampToDate = (timestamp) => {
    if (timestamp === "N/A") return null;
    const [datePart, timePart] = timestamp.split(" ");
    const [day, month, year] = datePart.split("/").map(Number);
    const [hour, minute] = timePart.split(":").map(Number);
    return new Date(year, month - 1, day, hour, minute);
  };

  const calculateTimeDifference = (lastTimestamp) => {
    if (lastTimestamp === "N/A") return "N/A";
    const lastDate = parseTimestampToDate(lastTimestamp);
    if (!lastDate) return "N/A";
    const now = new Date();
    const diffMs = now - lastDate;
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffMins < 60) {
      const diffSecs = Math.floor((diffMs % (1000 * 60)) / 1000);
      return `${diffMins} minute${diffMins !== 1 ? "s" : ""} ${diffSecs} second${diffSecs !== 1 ? "s" : ""}`;
    } else {
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours < 24) {
        const remainingMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ${remainingMins} minute${remainingMins !== 1 ? "s" : ""}`;
      } else {
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        if (diffDays < 30) {
          const remainingHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          return `${diffDays} day${diffDays !== 1 ? "s" : ""} ${remainingHours} hour${remainingHours !== 1 ? "s" : ""}`;
        } else {
          const diffMonths = Math.floor(diffDays / 30);
          if (diffMonths < 12) {
            const remainingDays = diffDays % 30;
            return `${diffMonths} month${diffMonths !== 1 ? "s" : ""} ${remainingDays} day${remainingDays !== 1 ? "s" : ""}`;
          } else {
            const diffYears = Math.floor(diffMonths / 12);
            const remainingMonths = diffMonths % 12;
            return `${diffYears} year${diffYears !== 1 ? "s" : ""} ${remainingMonths} month${remainingMonths !== 1 ? "s" : ""}`;
          }
        }
      }
    }
  };

  const fetchTempSensorCount = async () => {
    try {
      const response = await api.get(
        `/control-panel/getTempSensorCount?siteDb=${selectedSite}`
      );
      setSensorCount(parseInt(response.data.sensorCount) || 2); // Default to 2 if invalid
    } catch (error) {
      console.error("Error fetching temperature sensor count:", error);
      setSensorCount(2); // Default to 2 as per backend
    }
  };

  const fetchLatestTestTrain = async () => {
    try {
      const response = await api.get(
        `/control-panel/getLatestTestTrainData?siteDb=${selectedSite}`
      );
      const trainId = response.data.data.TrainId;
      const formattedTimestamp = formatTrainId(trainId);
      setLastTestTimestamp(formattedTimestamp);
      setTimeDifference(calculateTimeDifference(formattedTimestamp));
    } catch (error) {
      console.error("Error fetching latest test train data:", error);
      setLastTestTimestamp("N/A");
      setTimeDifference("N/A");
    }
  };

  useEffect(() => {
    if (selectedSite) {
      fetchTempSensorCount();
      if (showForm) {
        fetchLatestTestTrain();
      }
    }
  }, [showForm, selectedSite]);

  useEffect(() => {
    if (
      formValues.TypeOfLoco &&
      formValues.NoOfLoco &&
      formValues.NoOfCoaches
    ) {
      const noOfLoco = parseInt(formValues.NoOfLoco) || 0;
      const noOfCoaches = parseInt(formValues.NoOfCoaches) || 0;
      let axlesPerLoco = formValues.TypeOfLoco === "WAG-12" ? 4 : 6;
      let locoAxles = noOfLoco * axlesPerLoco;
      let calculatedAxles = locoAxles + 4 * noOfCoaches;

      if (calculatedAxles > 1000) {
        setMessage({
          text: "Total number of axles cannot exceed 1000",
          type: "error",
        });
        setShowAxleFields(false);
        setMaxAxles(0);
        setAxleNumbers([]);
        setFormValues((prev) => ({ ...prev, axles: [] }));
        return;
      }

      setMaxAxles(calculatedAxles);

      const numbers = [];
      for (let i = locoAxles + 1; i <= calculatedAxles; i++) {
        numbers.push(i);
      }
      setAxleNumbers(numbers);

      setShowAxleFields(true);
      setFormValues((prev) => ({
        ...prev,
        axles: prev.axles.length === 0 ? [{ axleNo: "", sideOfDefect: "", defectType: "", isDefective: false }] : prev.axles,
      }));
    } else {
      setShowAxleFields(false);
      setMaxAxles(0);
      setAxleNumbers([]);
      setFormValues((prev) => ({ ...prev, axles: [] }));
    }
  }, [formValues.TypeOfLoco, formValues.NoOfLoco, formValues.NoOfCoaches]);

  const handleChange = (e, index = null) => {
    if (index !== null) {
      const newAxles = [...formValues.axles];
      if (e.target.type === "checkbox") {
        newAxles[index][e.target.name] = e.target.checked;
      } else {
        newAxles[index][e.target.name] = e.target.value;
      }
      setFormValues({ ...formValues, axles: newAxles });
    } else {
      const { name, value } = e.target;
      if (name === "NoOfLoco") {
        const locoValue = parseInt(value) || 0;
        if (locoValue > 2) {
          setMessage({
            text: "Number of locomotives cannot exceed 2",
            type: "error",
          });
          return;
        }
      }
      setFormValues({ ...formValues, [name]: value });
      setMessage({ text: "", type: "" });
    }
  };

  const handleAddAxle = () => {
    if (formValues.axles.length < Math.min(maxAxles, 5)) {
      setFormValues({
        ...formValues,
        axles: [
          ...formValues.axles,
          { axleNo: "", sideOfDefect: "", defectType: "", isDefective: false },
        ],
      });
    }
  };

  const isFormValid = () => {
    const mainFieldsValid =
      formValues.TypeOfLoco &&
      formValues.NoOfLoco &&
      formValues.NoOfCoaches &&
      formValues.Description &&
      formValues.Speed &&
      formValues.AtmosphericTemp;

    // Check for duplicate defective axles
    const defectiveAxles = formValues.axles.filter((axle) => axle.isDefective);
    const axleSet = new Set();
    for (const axle of defectiveAxles) {
      const key = `${axle.axleNo}-${axle.sideOfDefect}-${axle.defectType}`;
      if (axleSet.has(key)) {
        setMessage({
          text: "Duplicate defective axle entries detected (same axle number, side, and defect type)",
          type: "error",
        });
        return false;
      }
      if (axle.axleNo && axle.sideOfDefect && axle.defectType) {
        axleSet.add(key);
      } else if (axle.isDefective) {
        setMessage({
          text: "All fields (axle number, side, defect type) must be filled for defective axles",
          type: "error",
        });
        return false;
      }
    }

    return mainFieldsValid;
  };

  const isTimeDifferenceValid = () => {
    if (lastTestTimestamp === "N/A") return true;
    const lastDate = parseTimestampToDate(lastTestTimestamp);
    if (!lastDate) return true;
    const now = new Date();
    const diffMs = now - lastDate;
    const diffMins = diffMs / (1000 * 60);
    return diffMins >= 3;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid()) {
      return;
    }

    if (!isTimeDifferenceValid()) {
      setMessage({
        text: "Test train simulation must be at least 3 minutes apart from the last test",
        type: "error",
      });
      return;
    }

    setLoading(true);
    setMessage({ text: "", type: "" });

    try {
      const payload = {
        siteDb: selectedSite,
        NoLoco: parseInt(formValues.NoOfLoco),
        NoBogi: parseInt(formValues.NoOfCoaches),
        TotalNoOfAxles:
          (formValues.TypeOfLoco === "WAG-12" ? 4 : 6) * parseInt(formValues.NoOfLoco) +
          4 * parseInt(formValues.NoOfCoaches),
        AtmosphereTemp: parseFloat(formValues.AtmosphericTemp),
        Description: formValues.Description,
        TypeOfLoco: formValues.TypeOfLoco === "WAG-12" ? "WAG12" : "LOCO",
        axles: formValues.axles
          .filter((axle) => axle.isDefective && axle.axleNo && axle.sideOfDefect && axle.defectType)
          .map((axle) => ({
            axleNo: parseInt(axle.axleNo),
            sideOfDefect: axle.sideOfDefect === "LH" ? "Left" : "Right",
            defectType: axle.defectType,
            isDefective: axle.isDefective,
          })),
      };

      const response = await api.post(
        "/control-panel/insertTestTrainTransaction",
        payload
      );

      setMessage({
        text: "Test train data inserted successfully!",
        type: "success",
      });

      setTestResult({
        trainName: response.data.trainName || "Test Train Simulation",
        trainNumber: response.data.trainNumber || "123456",
        trainDateTime: formatTrainDateTime(
          response.data.trainTransactionInsertedId
        ),
        TypeOfLoco: formValues.TypeOfLoco,
        NoOfLoco: formValues.NoOfLoco,
        NoOfCoaches: formValues.NoOfCoaches,
        Description: formValues.Description,
      });

      setFormValues({
        TypeOfLoco: "",
        NoOfLoco: "",
        NoOfCoaches: "",
        Description: "WAGON",
        Speed: "",
        AtmosphericTemp: "",
        axles: [],
      });

      setShowForm(false);
      await fetchLatestTestTrain();
    } catch (error) {
      console.error("Error inserting test train data:", error);
      setMessage({
        text: error.response?.data?.message || "Failed to insert test data",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNewTest = () => {
    setTestResult(null);
    setShowForm(true);
    setShowAxleFields(false);
    setMessage({ text: "", type: "" });
  };

  useEffect(() => {
    if (message.type === "success") {
      const timer = setTimeout(() => {
        setMessage({ text: "", type: "" });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <div className="container mx-auto p-2">
      <style>
        {`
          .primary-row {
            background-color: #f0f9ff;
            padding: 1rem;
            border-radius: 0.5rem;
            margin-bottom: 0.5rem;
          }
          .axle-row {
            background-color: #f9fafb;
            padding: 0.75rem;
            border: 1px solid #e5e7eb;
            border-radius: 0.375rem;
            margin-bottom: 0.5rem;
          }
          .axle-row-header {
            font-weight: 600;
            color: #1e40af;
          }
        `}
      </style>

      <div className="bg-blue-50 p-4 rounded-lg mb-2">
        <h2 className="text-lg font-bold text-blue-800">Self Test</h2>
        <p className="text-xs text-gray-600">
          Run the primary self-test to check the status of your system.
        </p>
      </div>

      {message.text && (
        <div
          className={`mb-2 p-2 rounded text-sm ${
            message.type === "success"
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {message.text}
        </div>
      )}

      {showForm ? (
        <div className="bg-gray-100 p-4 rounded-lg mb-4">
          <form onSubmit={handleSubmit}>
            <div className="flex flex-wrap gap-4 mb-2 primary-row">
              <div className="w-full sm:w-[31%]">
                <label className="text-blue-900 font-semibold text-xs">
                  Type of Locomotive
                </label>
                <select
                  name="TypeOfLoco"
                  value={formValues.TypeOfLoco}
                  onChange={handleChange}
                  className="w-full p-2 border rounded text-xs mt-1"
                  required
                >
                  <option value="">Select Type</option>
                  <option value="LOCO">LOCO</option>
                  <option value="WAG-12">WAG-12</option>
                </select>
              </div>

              <div className="w-full sm:w-[31%]">
                <label className="text-blue-900 font-semibold text-xs">
                  No. of Locomotives
                </label>
                <input
                  type="number"
                  name="NoOfLoco"
                  value={formValues.NoOfLoco}
                  onChange={handleChange}
                  className="w-full p-2 border rounded text-xs mt-1"
                  placeholder="No. of Locomotives"
                  required
                  min="0"
                  max="2"
                />
              </div>

              <div className="w-full sm:w-[31%]">
                <label className="text-blue-900 font-semibold text-xs">
                  No. of Coaches
                </label>
                <input
                  type="number"
                  name="NoOfCoaches"
                  value={formValues.NoOfCoaches}
                  onChange={handleChange}
                  className="w-full p-2 border rounded text-xs mt-1"
                  placeholder="No. of Coaches"
                  required
                  min="0"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-4 mb-2 primary-row">
              <div className="w-full sm:w-[31%]">
                <label className="text-blue-900 font-semibold text-xs">
                  Coach Description
                </label>
                <select
                  name="Description"
                  value={formValues.Description}
                  onChange={handleChange}
                  className="w-full p-2 border rounded text-xs mt-1"
                  required
                >
                  <option value="WAGON">WAGON</option>
                  <option value="ICF">ICF</option>
                  <option value="EMU">EMU</option>
                  <option value="LHB">LHB</option>
                  <option value="Vande Bharat">Vande Bharat</option>
                </select>
              </div>

              <div className="w-full sm:w-[31%]">
                <label className="text-blue-900 font-semibold text-xs">
                  Speed (in Kmph)
                </label>
                <input
                  type="number"
                  name="Speed"
                  value={formValues.Speed}
                  onChange={handleChange}
                  className="w-full p-2 border rounded text-xs mt-1"
                  placeholder="Speed"
                  required
                  min="1"
                  step="0.1"
                />
              </div>

              <div className="w-full sm:w-[31%]">
                <label className="text-blue-900 font-semibold text-xs">
                  Ambient Temp (in °C)
                </label>
                <input
                  type="number"
                  name="AtmosphericTemp"
                  value={formValues.AtmosphericTemp}
                  onChange={handleChange}
                  className="w-full p-2 border rounded text-xs mt-1"
                  placeholder="Ambient Temp (in °C)"
                  required
                  min="-50"
                  max="100"
                  step="0.1"
                />
              </div>
            </div>

            {showAxleFields && (
              <div className="mb-2">
                <div className="flex flex-wrap gap-2 mb-1 axle-row">
                  <div className="w-full sm:w-[10%] flex-shrink-0">
                    <label className="text-blue-900 font-semibold text-xs axle-row-header">
                      Sr. No.
                    </label>
                  </div>
                  <div className="w-full sm:w-[20%] flex-shrink-0">
                    <label className="text-blue-900 font-semibold text-xs axle-row-header">
                      Axle No.
                    </label>
                  </div>
                  <div className="w-full sm:w-[20%] flex-shrink-0">
                    <label className="text-blue-900 font-semibold text-xs axle-row-header">
                      Side of Defect
                    </label>
                  </div>
                  <div className="w-full sm:w-[20%] flex-shrink-0">
                    <label className="text-blue-900 font-semibold text-xs axle-row-header">
                      Defect Type
                    </label>
                  </div>
                  <div className="w-full sm:w-[20%] flex-shrink-0 flex justify-center">
                    <label className="text-blue-900 font-semibold text-xs axle-row-header text-center">
                      Defective
                    </label>
                  </div>
                </div>
                {formValues.axles.map((axle, index) => (
                  <div key={index} className="flex flex-wrap gap-2 axle-row">
                    <div className="w-full sm:w-[10%] flex-shrink-0 flex items-center">
                      <span className="text-xs font-semibold">{index + 1}</span>
                    </div>
                    <div className="w-full sm:w-[20%] flex-shrink-0">
                      <select
                        name="axleNo"
                        value={axle.axleNo}
                        onChange={(e) => handleChange(e, index)}
                        className="w-full p-2 border rounded text-xs"
                      >
                        <option value="">Select Axle No.</option>
                        {axleNumbers.map((num) => (
                          <option key={num} value={num}>
                            {num}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="w-full sm:w-[20%] flex-shrink-0">
                      <select
                        name="sideOfDefect"
                        value={axle.sideOfDefect}
                        onChange={(e) => handleChange(e, index)}
                        className="w-full p-2 border rounded text-xs"
                      >
                        <option value="">Select Side</option>
                        <option value="LH">Left</option>
                        <option value="RH">Right</option>
                      </select>
                    </div>
                    <div className="w-full sm:w-[20%] flex-shrink-0">
                      <select
                        name="defectType"
                        value={axle.defectType}
                        onChange={(e) => handleChange(e, index)}
                        className="w-full p-2 border rounded text-xs"
                      >
                        <option value="">Select Defect Type</option>
                        {getFilteredDefectTypes().map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.value}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="w-full sm:w-[20%] flex-shrink-0 flex justify-center items-center">
                      <input
                        type="checkbox"
                        name="isDefective"
                        checked={axle.isDefective}
                        onChange={(e) => handleChange(e, index)}
                        className="h-4 w-4"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-center items-center mt-4 gap-4">
              {showAxleFields && formValues.axles.length < Math.min(maxAxles, 5) && (
                <button
                  type="button"
                  onClick={handleAddAxle}
                  className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 text-xs"
                >
                  Add More Axle
                </button>
              )}

              <button
                type="submit"
                className={`bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 text-xs ${
                  loading || !isFormValid() || !isTimeDifferenceValid()
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
                disabled={loading || !isFormValid() || !isTimeDifferenceValid()}
              >
                {loading ? "Running Test Train..." : "Run Test Train"}
              </button>
            </div>

            <div className="text-center mt-2">
              <p className="text-xs text-gray-600">
                Last Test Train Simulation passed on {lastTestTimestamp}
                {timeDifference !== "N/A" && (
                  <span className="block">
                    Time Since Last Test: {timeDifference}
                  </span>
                )}
                {maxAxles > 0 && (
                  <span className="block">
                    Max Axles Allowed: {Math.min(maxAxles, 5)}
                  </span>
                )}
              </p>
            </div>
          </form>
        </div>
      ) : (
        <div className="bg-gray-100 p-6 rounded-lg mb-4">
          <div className="space-y-3 text-sm">
            <div className="flex justify-between border-b pb-2">
              <span className="font-semibold text-blue-900">Train Name:</span>
              <span>{testResult?.trainName}</span>
            </div>
            <div className="flex justify-between border-b pb-2">
              <span className="font-semibold text-blue-900">Train Number:</span>
              <span>{testResult?.trainNumber}</span>
            </div>
            <div className="flex justify-between border-b pb-2">
              <span className="font-semibold text-blue-900">
                Train Time & Date:
              </span>
              <span>{testResult?.trainDateTime}</span>
            </div>
            <div className="flex justify-between border-b pb-2">
              <span className="font-semibold text-blue-900">
                Type of Locomotive:
              </span>
              <span>{testResult?.TypeOfLoco}</span>
            </div>
            <div className="flex justify-between border-b pb-2">
              <span className="font-semibold text-blue-900">
                No. of Locomotives:
              </span>
              <span>{testResult?.NoOfLoco}</span>
            </div>
            <div className="flex justify-between border-b pb-2">
              <span className="font-semibold text-blue-900">
                No. of Coaches:
              </span>
              <span>{testResult?.NoOfCoaches}</span>
            </div>
            <div className="flex justify-between border-b pb-2">
              <span className="font-semibold text-blue-900">
                Coach Description:
              </span>
              <span>{testResult?.Description}</span>
            </div>
          </div>

          <div className="text-center mt-6">
            <button
              onClick={handleNewTest}
              className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 text-xs"
            >
              Run Another Test Train
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SelfTest;